/**
 * Identity of a row, column, or cell within a table, derived from table props. Cell identity is
 * composed from row and column identity, so all three live together here rather than in separate
 * per-axis files.
 */

import {TableIndex} from 'notebook/tables/constants/TableSelectionConstants';

/**
 * Per-row identity and render metadata derived from table props.
 */
export interface RowIdentity {
  /**
   * Whether this row is the table's header row (row 0 when `table.headerRow` is set). Used by the
   * row when rendering ghost cells; per-cell header styling uses {@link CellModel.isHeaderCell}.
   */
  isHeaderRow: boolean;
}

/**
 * The `data-cid` of the cell at (`rowIndex`, `columnIndex`). `tableCid` is the table's own cid.
 *
 * Cell cids are used both as React-independent DOM handles (`td[data-cid=...]`) and as text
 * selection targets, so the render path and every lookup must build them the same way.
 */
export function computeCellCid(tableCid: string, rowIndex: number, columnIndex: number): string {
  return `table-${tableCid}-${rowIndex}-${columnIndex}`;
}

/**
 * The cell coordinates encoded in `cid`, or null when `cid` is not a cell cid for `tableCid`.
 * The inverse of {@link computeCellCid}.
 */
export function computeCellIndexFromCid(tableCid: string, cid: string): TableIndex | null {
  const match = new RegExp(`table-${tableCid}-(\\d+)-(\\d+)`).exec(cid);
  if (match == null) {
    return null;
  }
  return [parseInt(match[1], 10), parseInt(match[2], 10)];
}

/** The custom row label at `rowIndex`, or null when none is defined. */
export function computeCustomRowLabel(
  rowLabels: Array<JSX.Element | string> | null | undefined,
  rowIndex: number
): JSX.Element | string | null {
  return (rowLabels && rowLabels[rowIndex]) || null;
}

/** Whether `rowIndex` is the table header row. */
export function computeIsHeaderRow(rowIndex: number, headerRow: boolean | null | undefined): boolean {
  return rowIndex === 0 && Boolean(headerRow);
}

/** Whether `columnIndex` is the table header column. */
export function computeIsHeaderColumn(columnIndex: number, headerCol: boolean | null | undefined): boolean {
  return columnIndex === 0 && Boolean(headerCol);
}

/**
 * Whether the cell at (`rowIndex`, `columnIndex`) renders as a header cell: either it is in the
 * header row, or it is the first column of a table with a header column.
 */
export function computeIsHeaderCell(
  rowIndex: number,
  columnIndex: number,
  headerRow: boolean | null | undefined,
  headerCol: boolean | null | undefined
): boolean {
  return computeIsHeaderRow(rowIndex, headerRow) || computeIsHeaderColumn(columnIndex, headerCol);
}

/** Cell highlight classes for `rowIndex`, or null when cell colors are not provided. */
export function computeRowCellColors(
  cellColors: {[rowIndex: number]: {[columnIndex: number]: string}} | null | undefined,
  rowIndex: number
): {[columnIndex: number]: string} | null {
  return cellColors?.[rowIndex] ?? null;
}
